'use client';

import { useId, useState } from 'react';

/**
 * The form controls, in one place.
 *
 * Every app had its own idea of what an input is: a bare `<input className="field">`
 * here, a wrapper with a button inside it there, and a third shape in a dialog.
 * They drifted, and a fix to one of them fixed one screen. These are the whole
 * vocabulary now, and the styles they carry live in `globals.css` beside the
 * dropdown, so changing how a field looks is one edit for both apps.
 *
 * They are deliberately thin. Everything a real `<input>` accepts is passed
 * straight through, so nothing has to be added here before a page can use a
 * pattern, and no page is tempted to go around them.
 */

type NativeInput = React.InputHTMLAttributes<HTMLInputElement>;
type NativeTextarea = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

/** Label, control, and the line under it that explains or complains. */
function Wrapper({
  id,
  label,
  labelAction,
  hint,
  error,
  required,
  children,
}: {
  id: string;
  label?: string;
  /**
   * A small link at the far end of the label's own line: "Forgot?" beside
   * Password, "What is this?" beside a field nobody recognises.
   *
   * There rather than under the form, because that is where the thought
   * happens. Somebody realises they have forgotten a password while looking
   * at the box for it, and a link at the bottom of the card is found by going
   * back and reading everything again.
   */
  labelAction?: React.ReactNode;
  hint?: string;
  error?: string | null;
  /** Marks the label with `*` — the only place a field says it cannot be left
   * blank. */
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="fieldrow">
      {label && (
        <div className="fieldrow-head">
          <label className="label" htmlFor={id}>
            {label}
            {required && (
              <span className="fieldrow-required" aria-hidden>
                {' '}
                *
              </span>
            )}
          </label>
          {labelAction}
        </div>
      )}
      {children}
      {/* Always on screen, even with nothing to say: a field that gains a line
          of error text when it fails and loses it again once fixed would push
          every field below it up and down as she types. The reserved line
          holds the hint when there is one and stays blank when there is
          neither, so only the message itself, never the layout, changes. */}
      <p className={error ? 'fieldrow-error' : 'fieldrow-hint'}>{error ?? hint ?? ' '}</p>
    </div>
  );
}

export function Input({
  label,
  labelAction,
  hint,
  error,
  id,
  className = '',
  ...rest
}: NativeInput & {
  label?: string;
  labelAction?: React.ReactNode;
  hint?: string;
  error?: string | null;
}) {
  const generated = useId();
  const inputId = id ?? generated;

  return (
    <Wrapper
      id={inputId}
      label={label}
      labelAction={labelAction}
      hint={hint}
      error={error}
      required={rest.required}
    >
      <input
        id={inputId}
        className={`field ${error ? 'is-invalid' : ''} ${className}`.trim()}
        spellCheck={false}
        {...rest}
      />
    </Wrapper>
  );
}

/**
 * A password field with its own reveal toggle. Only settings' key-edit
 * dialog had this eye before, hand-built for that one dialog; a field
 * where mistyping is the whole reason to ask twice (a new password, typed
 * blind, twice) needed it just as much and had nothing.
 */
export function PasswordInput({
  label,
  labelAction,
  hint,
  error,
  id,
  className = '',
  ...rest
}: NativeInput & {
  label?: string;
  labelAction?: React.ReactNode;
  hint?: string;
  error?: string | null;
}) {
  const generated = useId();
  const inputId = id ?? generated;
  const [reveal, setReveal] = useState(false);

  return (
    <Wrapper
      id={inputId}
      label={label}
      labelAction={labelAction}
      hint={hint}
      error={error}
      required={rest.required}
    >
      <div className="field-wrap">
        <input
          id={inputId}
          className={`field-bare ${error ? 'is-invalid' : ''} ${className}`.trim()}
          type={reveal ? 'text' : 'password'}
          spellCheck={false}
          {...rest}
        />
        {/* Out of tab order: Tab is moving between fields, not detouring
            through a toggle nobody reached for yet. Still reachable by mouse
            or touch, same as any other button on the row. */}
        <button
          type="button"
          tabIndex={-1}
          className="field-action"
          onClick={() => setReveal((r) => !r)}
          aria-pressed={reveal}
          aria-label={reveal ? 'Hide what you typed' : 'Show what you typed'}
          title={reveal ? 'Hide what you typed' : 'Show what you typed'}
        >
          <EyeIcon on={reveal} />
        </button>
      </div>
    </Wrapper>
  );
}

/** Struck through once the real value is showing, same glyph everywhere the panel reveals one. */
/** The reveal toggle, shared with the settings panel, which drew its own. */
export function EyeIcon({ on }: { on: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M1 8s2.5-4.5 7-4.5S15 8 15 8s-2.5 4.5-7 4.5S1 8 1 8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="1.7" fill="none" stroke="currentColor" strokeWidth="1.3" />
      {on && <path d="M2.5 13.5L13.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />}
    </svg>
  );
}

export function Textarea({
  label,
  hint,
  error,
  id,
  className = '',
  ...rest
}: NativeTextarea & { label?: string; hint?: string; error?: string | null }) {
  const generated = useId();
  const inputId = id ?? generated;

  return (
    <Wrapper id={inputId} label={label} hint={hint} error={error} required={rest.required}>
      <textarea
        id={inputId}
        className={`field ${error ? 'is-invalid' : ''} ${className}`.trim()}
        spellCheck={false}
        {...rest}
      />
    </Wrapper>
  );
}

/**
 * A fact about the thing being edited: a tick, a label, and a line saying what
 * ticking it means.
 *
 * The line is not optional. Every checkbox in this product turns something on
 * that has a consequence for a real person (whether an address is watched,
 * whether a notice carries a screenshot) and a bare label leaves the reader to
 * guess which way round it is.
 */
export function Checkbox({
  label,
  detail,
  checked,
  disabled = false,
  onChange,
}: {
  label: React.ReactNode;
  detail?: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={disabled ? 'checkrow is-disabled' : 'checkrow'}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="checkrow-text">
        <strong>{label}</strong>
        {detail && <span>{detail}</span>}
      </span>
    </label>
  );
}

/**
 * A setting that takes effect the moment it moves.
 *
 * Different from {@link Checkbox} because it behaves differently: a checkbox is
 * part of a form you are about to submit, a switch is the submission. It is
 * used where the change is saved on the spot, and its label says which of the
 * two states it is in rather than what the switch is called.
 */
export function Switch({
  state,
  checked,
  disabled = false,
  onChange,
  'aria-label': ariaLabel,
}: {
  /** What is true right now, not what flipping it would do. */
  state: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  'aria-label'?: string;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.checked)}
      />
      {/* The track and knob are drawn by the `i`; the real input sits off the
          screen so it keeps its tab stop and its announcement. */}
      <i aria-hidden />
      <span>{state}</span>
    </label>
  );
}

/**
 * A field with one control living inside it: the eye that shows a service key,
 * the arrow that suggests another password.
 *
 * The frame belongs to the wrapper, so the input inside draws no border of its
 * own and the two can never disagree about which of them is focused.
 */
export function InputWithAction({
  label,
  hint,
  error,
  id,
  className = '',
  action,
  ...rest
}: NativeInput & {
  label?: string;
  hint?: string;
  error?: string | null;
  action: { icon: React.ReactNode; label: string; onClick: () => void; pressed?: boolean };
}) {
  const generated = useId();
  const inputId = id ?? generated;

  return (
    <Wrapper id={inputId} label={label} hint={hint} error={error} required={rest.required}>
      <div className="field-wrap">
        <input
          id={inputId}
          className={`field-bare ${error ? 'is-invalid' : ''} ${className}`.trim()}
          spellCheck={false}
          {...rest}
        />
        <button
          type="button"
          className="field-action"
          onClick={action.onClick}
          aria-label={action.label}
          aria-pressed={action.pressed}
          title={action.label}
        >
          {action.icon}
        </button>
      </div>
    </Wrapper>
  );
}
